import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Box, Typography, Container, CircularProgress, Paper, 
  Alert, Button, Divider, Collapse, IconButton, Checkbox, Chip
} from "@mui/material";
import { ExpandMore, ExpandLess, DeleteOutline } from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import API_URLS from "../../../constants/apiConstants";
import PublicInfoCard from "./components/PublicInfoCard";
import PublicRoomsCard from "./components/PublicRoomsCard";
import PublicContactsSection from "./components/PublicContactsSection";
import { LanguageSwitcher, LoadingDots } from "../../../components/general";

const AdmissionFormPublic = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  // ─── Page-level state ───────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [metadata, setMetadata] = useState({ rooms: [], nationalities: [], genders: [] });

  // ─── Gate flow state ─────────────────────────────────────────────────────────
  // 'email'  → parent enters email, we check it
  // 'otp'    → email belongs to existing parent, they must verify
  // 'form'   → gate passed, show the full form
  const [gateStep, setGateStep] = useState('email');

  // Email gate
  const [gateEmail, setGateEmail] = useState('');
  const [gateEmailError, setGateEmailError] = useState('');
  const [gateChecking, setGateChecking] = useState(false);
  const [isExistingParent, setIsExistingParent] = useState(false);
  const [existingParentData, setExistingParentData] = useState(null);

  // OTP gate
  const [otpValue, setOtpValue] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  // After OTP verified — session token + parent's existing children
  const [admissionSessionToken, setAdmissionSessionToken] = useState(null);
  const [existingChildren, setExistingChildren] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState({});

  // ─── Form state ──────────────────────────────────────────────────────────────
  const [p1Errors, setP1Errors] = useState({});
  const [p2Errors, setP2Errors] = useState({});
  const [childrenErrors, setChildrenErrors] = useState([]);
  const [childGeneralError, setChildGeneralError] = useState(null);

  const [p2EmailChecked, setP2EmailChecked] = useState(false);
  const [p2ExistsMessage, setP2ExistsMessage] = useState(null);
  const [p2InvalidType, setP2InvalidType] = useState(false);

  const [contactsErrors, setContactsErrors] = useState([]);

  const [parent1, setParent1] = useState({ 
    isExpanded: true, 
    isExisting: false,
    p1_first_name: "", p1_last_name: "", p1_email: "",
    p1_phone: "", p1_address: "", p1_gender: "", 
  });
  
  const [parent2, setParent2] = useState({ 
    isExpanded: false, 
    isExisting: false,
    p2_first_name: "", p2_last_name: "", p2_email: "",
    p2_phone: "", p2_address: "", p2_gender: "", 
  });

  const [children, setChildren] = useState([{ 
    id: Date.now(), isExpanded: true,
    FirstName: "", LastName: "", BirthDate: "", Gender: "",
    Notes: "", Medications: "", DoctorName: "", DoctorPhone: "", Allergies: "",
    AppartmentHouse: "", ZipCode: "", City: "", State: "", Street: "", Classes: [] 
  }]);

  const [emergencyContacts, setEmergencyContacts] = useState([]);

  // ─── Load nursery data ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const validateRes = await axios.get(API_URLS.validateLink(token));
        setData(validateRes.data);
        if (!validateRes.data.valid) { setError(validateRes.data.error); return; }

        const metadataRes = await axios.get(API_URLS.fetchAdmissionMetaData(token));
        if (metadataRes.data.success) {
          setMetadata({
            rooms: metadataRes.data.rooms || [],
            nationalities: metadataRes.data.nationalities || [],
            genders: metadataRes.data.genders || []
          });
        } else {
          setError(metadataRes.data.error || t('admission_form.errorLoadingData'));
        }
      } catch (err) {
        if (err.response?.status === 404) { setNotFound(true); return; }
        setError(err.response?.data?.error || t('admission_form.connectionError'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // ─── Gate: Step 1 — Check email ──────────────────────────────────────────────
  const handleCheckEmail = async () => {
    setGateEmailError('');
    if (!gateEmail.trim()) {
      setGateEmailError(t('admission_form.emailRequired'));
      return;
    }
    if (!/\S+@\S+\.\S+/.test(gateEmail)) {
      setGateEmailError(t('admission_form.emailInvalid'));
      return;
    }

    setGateChecking(true);
    try {
      const res = await axios.get(API_URLS.checkParentEmail(gateEmail));
      const checkStatus = res.data.status;

      if (checkStatus === "found") {
        setIsExistingParent(true);
        setGateStep('otp');
        handleSendOTP(gateEmail);
      } else if (checkStatus === "invalid_type") {
        // Email belongs to a non-parent account (staff/admin) — block, stay on email step
        setGateEmailError(t('admission_form.invalidTypeError'));
      } else {
        // 'not_found' — new parent
        setIsExistingParent(false);
        setParent1(prev => ({ ...prev, p1_email: gateEmail, isExisting: false }));
        setGateStep('otp');
      }
    } catch (err) {
      // API unavailable — treat as new parent
      setIsExistingParent(false);
      setParent1(prev => ({ ...prev, p1_email: gateEmail, isExisting: false }));
      setGateStep('otp');
    } finally {
      setGateChecking(false);
    }
  };

  // ─── Gate: Step 2 — Send OTP ─────────────────────────────────────────────────
  const handleSendOTP = async (email) => {
    setOtpSending(true);
    setOtpError('');
    try {
      await axios.post(API_URLS.sendAdmissionOTP(), { email: email || gateEmail });
      setOtpSent(true);
    } catch (err) {
      setOtpError(t('admission_form.otpSendError'));
    } finally {
      setOtpSending(false);
    }
  };

  // ─── Gate: Step 2 — Verify OTP ───────────────────────────────────────────────
  const handleVerifyOTP = async () => {
    if (otpValue.length < 6) {
      setOtpError(t('admission_form.otpIncomplete'));
      return;
    }
    setOtpVerifying(true);
    setOtpError('');
    try {
      const res = await axios.post(API_URLS.verifyAdmissionOTP(), { email: gateEmail, otp: otpValue });
      // Backend returns admission_session_token — store it for authenticated requests
      const sessionToken = res.data.admission_session_token;
      setAdmissionSessionToken(sessionToken);
      // Pre-fill parent 1 email (locked) — other fields come from my-children fetch
      setParent1(prev => ({
        ...prev,
        isExisting: true,
        p1_email: gateEmail,
      }));
      // Fetch parent's existing children using the session token
      try {
        const childrenRes = await axios.get(API_URLS.getMyChildrenForAdmission(token), {
          headers: { 'X-Admission-Session-Token': sessionToken }
        });
        setExistingChildren(childrenRes.data.children || []);
      } catch {
        setExistingChildren([]);
      }
      setGateStep('form');
    } catch (err) {
      setOtpError(t('admission_form.otpInvalid'));
    } finally {
      setOtpVerifying(false);
    }
  };

  // ─── Form: Parent 2 email check ──────────────────────────────────────────────
  const checkParent2Email = async (email) => {
    if (!email?.trim() || !email.includes('@')) {
      setParent2(prev => ({ ...prev, isExisting: false, p2_first_name: "", p2_last_name: "", p2_phone: "", p2_address: "", p2_gender: "" }));
      setP2EmailChecked(false);
      setP2ExistsMessage(null);
      setP2InvalidType(false);
      return;
    }
    try {
      const res = await axios.get(API_URLS.checkParentEmail(email));
      const status = res.data.status;

      if (status === 'found') {
        setParent2(prev => ({ ...prev, isExisting: true }));
        setP2EmailChecked(true);
        setP2ExistsMessage(t('admission_form.existingAccountFound'));
        setP2InvalidType(false);
        setP2Errors(prev => ({ ...prev, p2_email: null }));
      } else if (status === 'invalid_type') {
        // Email belongs to a non-parent account (staff/admin)
        setParent2(prev => ({ ...prev, isExisting: false }));
        setP2EmailChecked(false); // keeps the other P2 fields disabled
        setP2ExistsMessage(null);
        setP2InvalidType(true);
        setP2Errors(prev => ({ ...prev, p2_email: t('admission_form.invalidTypeError') }));
      } else {
        // not_found
        setParent2(prev => ({ ...prev, isExisting: false }));
        setP2EmailChecked(true);
        setP2ExistsMessage(null);
        setP2InvalidType(false);
        setP2Errors(prev => ({ ...prev, p2_email: null }));
      }
    } catch (err) {
      setP2EmailChecked(true);
      setP2InvalidType(false);
    }
  };

  useEffect(() => {
    const email = parent2.p2_email;
    if (!email?.trim() || !email.includes('@')) return;
    const timer = setTimeout(() => checkParent2Email(email), 700);
    return () => clearTimeout(timer);
  }, [parent2.p2_email]);

  // ─── Form helpers ────────────────────────────────────────────────────────────
  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector(
        ".Mui-error, [aria-invalid='true'], .MuiFormHelperText-root.Mui-error, .errorText, [data-error='true']"
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = el.matches("input, textarea, select") ? el : el.querySelector("input, textarea, select");
        input?.focus({ preventScroll: true });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 250);
  };

  const validateFrontEnd = () => {
    let isValid = true;
    const p1Errs = {}, p2Errs = {}, cErrs = [];
    const isEmpty = (v) => !v || (typeof v === 'string' && v.trim() === "");

    // Parent 1 — if existing parent verified via OTP, only email needed (backend uses session)
    if (isExistingParent) {
      // Nothing to validate — session token carries identity
    } else if (parent1.isExisting) {
      if (isEmpty(parent1.p1_email)) { p1Errs.p1_email = t('admission_form.required'); isValid = false; }
    } else {
      ['p1_first_name','p1_last_name','p1_email','p1_phone','p1_address','p1_gender'].forEach(f => {
        if (isEmpty(parent1[f])) { p1Errs[f] = t('admission_form.required'); isValid = false; }
      });
    }

    // Parent 2 (optional)
    const hasAnyP2 = ['p2_email','p2_first_name','p2_last_name','p2_phone','p2_address','p2_gender'].some(f => !isEmpty(parent2[f]));
    if (hasAnyP2) {
      if (isEmpty(parent2.p2_email)) {
        p2Errs.p2_email = t('admission_form.requiredP2');
        isValid = false;
      } else if (p2InvalidType) {
        // Email belongs to staff/admin — show only this error, skip name checks
        p2Errs.p2_email = t('admission_form.invalidTypeError');
        isValid = false;
      } else if (!parent2.isExisting) {
        // Only require name fields if P2 is a NEW parent (not found in system)
        if (isEmpty(parent2.p2_first_name)) { p2Errs.p2_first_name = t('admission_form.requiredP2'); isValid = false; }
        if (isEmpty(parent2.p2_last_name))  { p2Errs.p2_last_name  = t('admission_form.requiredP2'); isValid = false; }
      }
    }

    // Children
    // Existing parents may skip the "new child" form if they selected at least one existing child
    const canSkipBlankChildren = isExistingParent && selectedExistingCount > 0;

    children.forEach((child, i) => {
      const e = {};
      if (canSkipBlankChildren && isChildBlank(child)) { cErrs[i] = e; return; }
      if (isEmpty(child.FirstName)) e.FirstName = [t('admission_form.required')];
      if (isEmpty(child.LastName))  e.LastName  = [t('admission_form.required')];
      if (isEmpty(child.BirthDate)) e.BirthDate = [t('admission_form.required')];
      if (isEmpty(child.Gender))    e.Gender    = [t('admission_form.required')];
      cErrs[i] = e;
      if (Object.keys(e).length > 0) isValid = false;
    });

    // Emergency contacts: first name required
    const ctErrs = [];
    emergencyContacts.forEach((c, i) => {
      if (isEmpty(c.FirstName)) {
        ctErrs[i] = { FirstName: t('admission_form.required') };
        isValid = false;
      }
    });
    setContactsErrors(ctErrs);

    setP1Errors(p1Errs); setP2Errors(p2Errs); setChildrenErrors(cErrs);
    if (!isValid) {
      if (Object.keys(p1Errs).length > 0) setParent1(p => ({ ...p, isExpanded: true }));
      if (Object.keys(p2Errs).length > 0) setParent2(p => ({ ...p, isExpanded: true }));
      const newChildren = [...children];
      children.forEach((_, idx) => { if (cErrs[idx] && Object.keys(cErrs[idx]).length > 0) newChildren[idx].isExpanded = true; });
      setChildren(newChildren);
      scrollToFirstError();
    }
    return isValid;
  };

  const handleSubmit = async () => {
    setError(null); setP1Errors({}); setP2Errors({}); setChildrenErrors([]); setChildGeneralError(null); setContactsErrors([]);
    if (!validateFrontEnd()) { setError(t('admission_form.fillRequired')); return; }
    setSubmitting(true);

    // Strip phone values that are just a dial code with no local number (+20 alone = null)
    const cleanPhone = (v) => (!v || v.replace("+", "").length <= 3) ? null : v;
    const toClassIds = (list) =>
      Array.isArray(list) ? list.map(cl => (typeof cl === 'object' ? cl.id : cl)) : [];

    // New children (skip the blank form if an existing parent picked existing children)
    const newChildrenPayload = children
      .filter(c => !(isExistingParent && selectedExistingCount > 0 && isChildBlank(c)))
      .map(c => ({
        ...c,
        DoctorPhone: cleanPhone(c.DoctorPhone),
        Classes: toClassIds(c.Classes),
      }));

    // Existing children: { existing_student_id, Classes? }
    const existingChildrenPayload = Object.entries(selectedExisting).map(([id, v]) => {
      const classes = toClassIds(v.Classes);
      return {
        existing_student_id: Number(id),
        ...(classes.length > 0 ? { Classes: classes } : {}),
      };
    });

    const payload = {
      link_token: token,

      // Parent 1: verified session carries identity, so no p1_* fields (Cases 2/3/4/5)
      ...(isExistingParent ? {} : {
        p1_first_name: parent1.p1_first_name,
        p1_last_name: parent1.p1_last_name,
        p1_email: parent1.p1_email,
        p1_phone: cleanPhone(parent1.p1_phone),
        p1_address: parent1.p1_address,
        p1_gender: parent1.p1_gender,
      }),

      // Parent 2: existing account -> only p2_email (Case 4), new account -> full data (Case 5)
      ...(parent2.p2_email
        ? (parent2.isExisting
            ? { p2_email: parent2.p2_email }
            : {
                p2_first_name: parent2.p2_first_name,
                p2_last_name: parent2.p2_last_name,
                p2_email: parent2.p2_email,
                p2_phone: cleanPhone(parent2.p2_phone),
                p2_address: parent2.p2_address,
                p2_gender: parent2.p2_gender,
              })
        : {}),

      // Existing + new children in the same array (can be mixed)
      children: [...existingChildrenPayload, ...newChildrenPayload],

      contacts: emergencyContacts.map(c => ({
        ...c,
        Phone: cleanPhone(c.Phone),
        SignedUP: c.SignedUp || false
      }))
    };
    // Build headers — send session token if available (existing parent OTP flow)
    const headers = {};
    if (admissionSessionToken) {
      headers['X-Admission-Session-Token'] = admissionSessionToken;
    }
    try {
      await axios.post(API_URLS.submitForm(), payload, { headers });
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      if (err.response?.status === 400) {
        const se = err.response.data.errors || {};
        const sP1 = {}, sP2 = {};
        Object.keys(se).forEach(k => { if (k.startsWith('p1_')) sP1[k] = se[k][0]; if (k.startsWith('p2_')) sP2[k] = se[k][0]; });
        setP1Errors(sP1); setP2Errors(sP2);
        if (se.children) {
          if (Array.isArray(se.children) && se.children.length > 0) {
            typeof se.children[0] === 'string' ? setChildGeneralError(se.children[0]) : setChildrenErrors(se.children);
          } else setChildGeneralError(se.children[0] || se.children);
        }
        setError(t('admission_form.fixErrors'));
        scrollToFirstError();
      } else {
        setError(t('admission_form.submitFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getOptions = (list, useCode = false) => list.map(item => ({
    label: isAr ? (item.Name_ar || item.name || item.Name) : (item.Name_en || item.name || item.Name),
    value: useCode ? (item.code || item.id) : item.id
  }));

  const toggleParent = (num) => {
    if (num === 1) setParent1({ ...parent1, isExpanded: !parent1.isExpanded });
    else setParent2({ ...parent2, isExpanded: !parent2.isExpanded });
  };
  const toggleChild = (i) => { const c = [...children]; c[i].isExpanded = !c[i].isExpanded; setChildren(c); };
  const toggleExistingChild = (child) => {
    if (child.already_in_this_nursery) return; // locked
    setSelectedExisting(prev => {
      const next = { ...prev };
      if (next[child.id]) delete next[child.id];
      else next[child.id] = { Classes: [] };
      return next;
    });
  };

  const setExistingChildClasses = (childId, sel) => {
    setSelectedExisting(prev => ({ ...prev, [childId]: { ...prev[childId], Classes: sel } }));
  };

  const isChildBlank = (c) =>
    ![c.FirstName, c.LastName, c.BirthDate, c.Gender].some(v => v && String(v).trim() !== "");

  const selectedExistingCount = Object.keys(selectedExisting).length;
  const addNewChild = () => setChildren([...children, { id: Date.now(), isExpanded: true, FirstName: "", LastName: "", BirthDate: "", Gender: "", Notes: "", Medications: "", DoctorName: "", DoctorPhone: "", Allergies: "", AppartmentHouse: "", ZipCode: "", City: "", State: "", Street: "", Classes: [] }]);
  const removeChild = (id) => { if (children.length > 1) setChildren(children.filter(c => c.id !== id)); };

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (loading) return <LoadingDots />;

  const purple = "#5e35b1";
  const purpleDark = "#4527a0";
  const btnSx = { borderRadius: "10px", fontWeight: 700, bgcolor: purple, "&:hover": { bgcolor: purpleDark } };

  return (
    <Container maxWidth="md" sx={{ py: 4, direction: isAr ? "rtl" : "ltr", position: "relative" }}>

      {/* Language Switcher */}
      {!notFound && (
        <Box sx={{ position: "absolute", top: 20, right: isAr ? "auto" : 20, left: isAr ? 20 : "auto", zIndex: 1000 }}>
          <LanguageSwitcher />
        </Box>
      )}

      {/* Header */}
      {!notFound && (
        <Box sx={{ textAlign: "center", mb: 6, mt: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: "bold", color: purple }}>
            {t('admission_form.welcomeTo')}
          </Typography>
          <Typography variant="h4" sx={{ color: "#7b1fa2", fontWeight: "500", mb: 2 }}>
            {data?.nursery_name || "Our Nursery"}
          </Typography>
          {data?.title && (
            <Box sx={{ display: "inline-block", backgroundColor: "rgba(123,31,162,0.08)", color: "#7b1fa2", padding: "8px 24px", borderRadius: "50px", border: "1px solid rgba(123,31,162,0.2)", fontSize: "1.1rem", fontWeight: "500", mb: 3 }}>
              {data.title}
            </Box>
          )}
        </Box>
      )}

      {/* ── 404 ── */}
      {notFound ? (
        <Paper elevation={4} sx={{ p: { xs: 4, md: 6 }, borderRadius: 3, textAlign: "center" }}>
          <Typography variant="h1" sx={{ fontSize: "120px", fontWeight: "bold", color: purple, mb: 2 }}>404</Typography>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#666", mb: 2 }}>Page Not Found</Typography>
          <Typography variant="body1" sx={{ color: "#999" }}>Sorry, the link you're looking for doesn't exist or has been removed.</Typography>
        </Paper>

      /* ── Invalid link ── */
      ) : !data?.valid ? (
        <Paper elevation={4} sx={{ p: { xs: 2, md: 5 }, borderRadius: 3, textAlign: "center" }}>
          <Alert severity="error" sx={{ mb: 2, direction: "ltr", textAlign: "left" }}>
            {error || (isAr ? "رابط غير صالح أو منتهي الصلاحية" : "Invalid or expired link")}
          </Alert>
        </Paper>

      /* ── Success ── */
      ) : success ? (
        <Paper elevation={4} sx={{ p: { xs: 4, md: 6 }, borderRadius: 3, textAlign: "center" }}>
          <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "#4caf50", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3 }}>
            <Typography variant="h2" sx={{ color: "white" }}>✓</Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", color: "#4caf50", mb: 2 }}>
            {t('admission_form.successTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: "#666", maxWidth: "500px", mx: "auto" }}>
            {t('admission_form.successMsg')}
          </Typography>
        </Paper>

      /* ── GATE: Step 1 — Email ── */
      ) : gateStep === 'email' ? (
        <Paper elevation={4} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3, maxWidth: 480, mx: "auto" }}>
          <Typography variant="h5" fontWeight={700} color={purple} mb={1} textAlign="center">
            {t('admission_form.enterEmail')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3} textAlign="center">
            {t('admission_form.emailGateSubtitle')}
          </Typography>

          <Typography variant="body2" fontWeight={600} mb={0.5}>
            {t('admission_form.emailLabel')} <span style={{ color: "red" }}>*</span>
          </Typography>
          <input
            type="email"
            value={gateEmail}
            onChange={e => { setGateEmail(e.target.value); setGateEmailError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleCheckEmail(); }}
            placeholder="example@mail.com"
            disabled={gateChecking}
            style={{
              width: "100%", padding: "10px 14px", borderRadius: "8px",
              border: gateEmailError ? "1px solid red" : "1px solid #ccc",
              fontSize: "1rem", marginBottom: "6px", boxSizing: "border-box"
            }}
          />
          {gateEmailError && <Typography variant="caption" color="error" display="block" mb={1}>{gateEmailError}</Typography>}

          <Button
            fullWidth
            variant="contained"
            onClick={handleCheckEmail}
            disabled={gateChecking}
            startIcon={gateChecking ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ ...btnSx, mt: 2, py: 1.3 }}
          >
            {gateChecking
              ? t('admission_form.checking')
              : t('admission_form.continue')}
          </Button>
        </Paper>

      /* ── GATE: Step 2 — OTP ── */
      ) : gateStep === 'otp' ? (
        <Paper elevation={4} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3, maxWidth: 480, mx: "auto" }}>

          {/* Show welcome back only for existing parents */}
          {isExistingParent ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('admission_form.welcomeBack', { email: gateEmail })}
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('admission_form.newParentMsg')}
            </Alert>
          )}

          <Typography variant="h5" fontWeight={700} color={purple} mb={1} textAlign="center">
            {isExistingParent ? t('admission_form.verifyIdentity') : t('admission_form.continueToForm')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3} textAlign="center">
            {isExistingParent
              ? (otpSent ? t('admission_form.otpSentMsg') : t('admission_form.otpWillSend'))
              : t('admission_form.newParentSubtitle')}
          </Typography>

          {/* OTP flow — only for existing parents */}
          {isExistingParent && (
            <>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpValue}
                onChange={e => { setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') handleVerifyOTP(); }}
                placeholder="• • • • • •"
                disabled={otpVerifying || !otpSent}
                style={{
                  width: "100%", padding: "14px", borderRadius: "8px",
                  border: otpError ? "1px solid red" : "1px solid #ccc",
                  fontSize: "1.6rem", letterSpacing: "0.5em", textAlign: "center",
                  marginBottom: "6px", boxSizing: "border-box",
                  background: !otpSent ? "#f5f5f5" : "white"
                }}
              />
              {otpError && <Typography variant="caption" color="error" display="block" mb={1}>{otpError}</Typography>}

              <Button
                fullWidth variant="contained"
                onClick={handleVerifyOTP}
                disabled={otpVerifying || !otpSent || otpValue.length < 6}
                startIcon={otpVerifying ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ ...btnSx, mt: 2, py: 1.3 }}
              >
                {otpVerifying ? t('admission_form.verifying') : t('admission_form.verify')}
              </Button>

              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Button
                  variant="text" size="small" disabled={otpSending}
                  onClick={() => handleSendOTP(gateEmail)}
                  sx={{ color: purple, fontWeight: 600 }}
                >
                  {otpSending
                    ? t('admission_form.sending')
                    : otpSent ? t('admission_form.resendCode') : t('admission_form.sendCode')}
                </Button>
                <Button
                  variant="text" size="small"
                  onClick={() => { setGateStep('email'); setOtpValue(''); setOtpError(''); setOtpSent(false); }}
                  sx={{ color: "text.secondary" }}
                >
                  {t('admission_form.changeEmail')}
                </Button>
              </Box>
            </>
          )}

          {/* New parent — just go to form */}
          {!isExistingParent && (
            <>
              <Button
                fullWidth variant="contained"
                onClick={() => {
                  setParent1(prev => ({ ...prev, p1_email: gateEmail, isExisting: false }));
                  setGateStep('form');
                }}
                sx={{ ...btnSx, py: 1.3 }}
              >
                {t('admission_form.continueToFormBtn')}
              </Button>
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  variant="text" size="small"
                  onClick={() => { setGateStep('email'); setOtpValue(''); setOtpError(''); setOtpSent(false); }}
                  sx={{ color: "text.secondary" }}
                >
                  {t('admission_form.changeEmail')}
                </Button>
              </Box>
            </>
          )}
        </Paper>

      /* ── FORM ── */
      ) : (
        <Paper elevation={4} sx={{ p: { xs: 2, md: 5 }, borderRadius: 3 }}>
          <Box>

            {/* Parents Section */}
            <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold", borderLeft: isAr ? 0 : "5px solid #5e35b1", borderRight: isAr ? "5px solid #5e35b1" : 0, px: 2 }}>
              {t('admission_form.parentsInfo')}
            </Typography>

            {[1, 2].map((num) => {
              const p = num === 1 ? parent1 : parent2;
              const errs = num === 1 ? p1Errors : p2Errors;
              const setP = num === 1 ? setParent1 : setParent2;
              const prefix = `p${num}_`;
              // P1 email is always checked (came from the gate), P2 uses its own check
              const emailChecked = num === 1 ? true : p2EmailChecked;
              const existsMessage = num === 1 ? null : p2ExistsMessage;

              return (
                <Paper key={num} sx={{ mb: 2, overflow: "hidden", borderRadius: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, cursor: "pointer", bgcolor: "#f3e5f5" }} onClick={() => toggleParent(num)}>
                    <Typography variant="h6">
                      {num === 1
                        ? t('admission_form.parent1Details')
                        : t('admission_form.parent2Details')}
                    </Typography>
                    {p.isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>

                  <Collapse in={p.isExpanded}>
                    <Box sx={{ p: 2 }}>
                      {existsMessage && <Alert severity="success" sx={{ mb: 2 }}>{existsMessage}</Alert>}
                      {num === 1 && isExistingParent && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {t('admission_form.identityVerified')}
                        </Alert>
                      )}

                      {/* For existing Parent 1 — only show locked email, no other fields needed */}
                      {num === 1 && isExistingParent ? (
                        <PublicInfoCard
                          title=""
                          fields={[{
                            label: t('admission_form.fieldEmail'),
                            key: `${prefix}email`,
                            value: p[`${prefix}email`],
                            type: "email",
                            required: true,
                            disabled: true,
                            error: errs[`${prefix}email`],
                          }]}
                          onFieldChange={() => {}}
                        />
                      ) : (
                        <PublicInfoCard
                          title=""
                          fields={[
                            // P2 gets email_check type, P1 is always locked (came from gate)
                            num === 2 ? {
                              label: t('admission_form.fieldEmail'),
                              key: `${prefix}email`,
                              value: p[`${prefix}email`],
                              type: "email_check",
                              required: false,
                              disabled: false,
                              error: errs[`${prefix}email`],
                              checking: false,
                              checkResult: p2EmailChecked ? (p.isExisting ? "found" : "not_found") : null,
                              onChange: (val) => {
                                setParent2(prev => ({ ...prev, p2_email: val, isExisting: false }));
                                setP2EmailChecked(false); setP2ExistsMessage(null);
                                setP2InvalidType(false);
                                setP2Errors(prev => ({ ...prev, p2_email: null }));
                              },
                            } : {
                              label: t('admission_form.fieldEmail'),
                              key: `${prefix}email`,
                              value: p[`${prefix}email`],
                              type: "email",
                              required: true,
                              disabled: true,
                              error: errs[`${prefix}email`],
                            },
                            {
                              label: t('admission_form.fieldFirstName'),
                              key: `${prefix}first_name`, value: p[`${prefix}first_name`],
                              required: num === 1, disabled: !emailChecked || p.isExisting,
                              error: errs[`${prefix}first_name`]
                            },
                            {
                              label: t('admission_form.fieldLastName'),
                              key: `${prefix}last_name`, value: p[`${prefix}last_name`],
                              required: num === 1, disabled: !emailChecked || p.isExisting,
                              error: errs[`${prefix}last_name`]
                            },
                            {
                              label: t('admission_form.fieldAddress'),
                              key: `${prefix}address`, value: p[`${prefix}address`],
                              required: num === 1 && !p.isExisting, disabled: !emailChecked || p.isExisting,
                              error: errs[`${prefix}address`]
                            },
                            {
                              label: t('admission_form.fieldGender'),
                              key: `${prefix}gender`, value: p[`${prefix}gender`],
                              type: "select", options: getOptions(metadata.genders),
                              required: num === 1 && !p.isExisting, disabled: !emailChecked || p.isExisting,
                              error: errs[`${prefix}gender`]
                            },
                            {
                              label: t('admission_form.fieldPhone'),
                              key: `${prefix}phone`,
                              value: p[`${prefix}phone`],
                              type: "phone",
                              required: num === 1 && !p.isExisting,
                              disabled: !emailChecked || p.isExisting,
                              error: errs[`${prefix}phone`],
                              onChange: (val) => {
                                setP({ ...p, [`${prefix}phone`]: val });
                                if (num === 1) setP1Errors(prev => ({ ...prev, [`${prefix}phone`]: null }));
                                else setP2Errors(prev => ({ ...prev, [`${prefix}phone`]: null }));
                              }
                            },
                          ]}
                          onFieldChange={(key, val) => {
                            setP({ ...p, [key]: val });
                            if (num === 1) setP1Errors(prev => ({ ...prev, [key]: null }));
                            else setP2Errors(prev => ({ ...prev, [key]: null }));
                          }}
                        />
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
            {/* Existing children (only for verified existing parents) */}
              {isExistingParent && existingChildren.length > 0 && (
                <>
                  <Typography variant="h5" sx={{ mb: 1, mt: 5, fontWeight: "bold", borderLeft: isAr ? 0 : "5px solid #5e35b1", borderRight: isAr ? "5px solid #5e35b1" : 0, px: 2 }}>
                    {t('admission_form.existingChildrenInfo')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 2 }}>
                    {t('admission_form.existingChildrenHint')}
                  </Typography>

                  {existingChildren.map((child) => {
                    const locked = child.already_in_this_nursery;
                    const checked = locked || !!selectedExisting[child.id];
                    const otherNurseries = (child.nurseries || []);

                    return (
                      <Paper key={child.id} sx={{ mb: 2, overflow: "hidden", borderRadius: 3, border: "1px solid #e0e0e0", opacity: locked ? 0.85 : 1 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", p: 2, bgcolor: locked ? "#e8f5e9" : "#e8eaf6", cursor: locked ? "default" : "pointer" }}
                          onClick={() => toggleExistingChild(child)}
                        >
                          <Checkbox
                            checked={checked}
                            disabled={locked}
                            onChange={() => toggleExistingChild(child)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ color: purple, "&.Mui-checked": { color: purple } }}
                          />
                          <Box sx={{ flexGrow: 1, mx: 1 }}>
                            <Typography variant="h6">{child.first_name} {child.last_name}</Typography>
                            {child.birth_date && (
                              <Typography variant="caption" color="text.secondary">{child.birth_date}</Typography>
                            )}
                          </Box>
                          {locked && (
                            <Chip size="small" color="success" label={t('admission_form.alreadyInNursery')} />
                          )}
                        </Box>

                        {/* Optional class selection — only for ticked, not-yet-enrolled children */}
                        <Collapse in={!locked && !!selectedExisting[child.id]}>
                          <Box sx={{ p: 3 }}>
                            <PublicRoomsCard
                              title={t('admission_form.classSelectionOptional')}
                              allRooms={metadata.rooms.map(r => ({ id: r.id, ClassName: r.name }))}
                              selectedRooms={selectedExisting[child.id]?.Classes || []}
                              onSelectionChange={(sel) => setExistingChildClasses(child.id, sel)}
                            />
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </>
              )}

            {/* Children Section */}
            <Typography variant="h5" sx={{ mb: 3, mt: 5, fontWeight: "bold", borderLeft: isAr ? 0 : "5px solid #5e35b1", borderRight: isAr ? "5px solid #5e35b1" : 0, px: 2 }}>
              {isExistingParent && existingChildren.length > 0
                ? t('admission_form.addNewChildInfo')
                : t('admission_form.childrenInfo')}
            </Typography>

            {children.map((child, index) => {
              const childErr = childrenErrors[index] || {};
              return (
                <Paper key={child.id} sx={{ mb: 3, overflow: "hidden", borderRadius: 3, border: "1px solid #e0e0e0" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, bgcolor: "#e8eaf6" }}>
                    <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer", flexGrow: 1 }} onClick={() => toggleChild(index)}>
                      {child.isExpanded ? <ExpandLess /> : <ExpandMore />}
                      <Typography variant="h6" sx={{ ml: 1, mr: 1, color: "#1976d2" }}>
                        {child.FirstName ? `${child.FirstName} ${child.LastName}` : t('admission_form.childNumber', { n: index + 1 })}
                      </Typography>
                    </Box>
                    {children.length > 1 && (
                      <IconButton color="error" onClick={(e) => { e.stopPropagation(); removeChild(child.id); }}>
                        <DeleteOutline />
                      </IconButton>
                    )}
                  </Box>

                  <Collapse in={child.isExpanded}>
                    <Box sx={{ p: 3 }}>
                      <PublicInfoCard
                        title={t('admission_form.personalInfo')}
                        fields={[
                          { label: t('admission_form.fieldFirstName'), key: "FirstName", value: child.FirstName, required: true, error: childErr.FirstName?.[0] },
                          { label: t('admission_form.fieldLastName'), key: "LastName", value: child.LastName, required: true, error: childErr.LastName?.[0] },
                          { label: t('admission_form.fieldBirthDate'), key: "BirthDate", value: child.BirthDate, type: "date", required: true, error: childErr.BirthDate?.[0] },
                          { label: t('admission_form.fieldGender'), key: "Gender", value: child.Gender, type: "select", options: getOptions(metadata.genders), required: true, error: childErr.Gender?.[0] },
                        ]}
                        onFieldChange={(key, val) => {
                          const nc = [...children]; nc[index][key] = val; setChildren(nc);
                          if (childrenErrors[index]?.[key]) {
                            const ne = [...childrenErrors]; ne[index] = { ...ne[index] }; delete ne[index][key]; setChildrenErrors(ne);
                          }
                        }}
                      />

                      <PublicRoomsCard
                        title={t('admission_form.classSelectionOptional')}
                        allRooms={metadata.rooms.map(r => ({ id: r.id, ClassName: r.name }))}
                        selectedRooms={child.Classes || []}
                        onSelectionChange={(sel) => {
                          const nc = [...children]; nc[index].Classes = sel; setChildren(nc);
                          if (sel.length > 0 && childrenErrors[index]) {
                            const ne = [...childrenErrors]; ne[index] = { ...ne[index] }; delete ne[index].Classes; setChildrenErrors(ne);
                          }
                        }}
                      />
                      {childErr.Classes && <Typography variant="caption" color="error" sx={{ px: 2, display: "block", mt: 1 }}>{childErr.Classes[0]}</Typography>}

                      <PublicInfoCard
                        title={t('admission_form.medicalInfo')}
                        fields={[
                          { label: t('admission_form.fieldAllergies'), key: "Allergies", value: child.Allergies, error: childErr.Allergies?.[0] },
                          { label: t('admission_form.fieldMedications'), key: "Medications", value: child.Medications, error: childErr.Medications?.[0] },
                          { label: t('admission_form.fieldDoctorName'), key: "DoctorName", value: child.DoctorName, error: childErr.DoctorName?.[0] },
                          { label: t('admission_form.fieldDoctorPhone'), key: "DoctorPhone", value: child.DoctorPhone, type: "phone", error: childErr.DoctorPhone?.[0],
                            onChange: (val) => {
                              const nc = [...children]; nc[index]["DoctorPhone"] = val; setChildren(nc);
                            }
                          },
                          { label: t('admission_form.fieldNotes'), key: "Notes", value: child.Notes, error: childErr.Notes?.[0] },
                        ]}
                        onFieldChange={(key, val) => {
                          const nc = [...children]; nc[index][key] = val; setChildren(nc);
                          if (childrenErrors[index]?.[key]) {
                            const ne = [...childrenErrors]; ne[index] = { ...ne[index] }; delete ne[index][key]; setChildrenErrors(ne);
                          }
                        }}
                      />
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}

            <Button variant="outlined" color="primary" onClick={addNewChild} sx={{ mb: 2 }}>
              {t('admission_form.addChild')}
            </Button>

            {childGeneralError && <Alert severity="error" sx={{ mb: 4, mt: 1, textAlign: isAr ? "right" : "left" }}>{childGeneralError}</Alert>}

            <Divider sx={{ my: 4, borderColor: "rgba(110, 83, 125, 1)" }} />

            <PublicContactsSection
              title={t('admission_form.emergencyContacts')}
              contacts={emergencyContacts}
              onContactsChange={setEmergencyContacts}
              isAr={isAr}
              errors={contactsErrors}
              onClearError={(i, key) =>
                setContactsErrors(prev => {
                  const next = [...prev];
                  if (next[i]) next[i] = { ...next[i], [key]: null };
                  return next;
                })
              }
            />

            <Box sx={{ mt: 6, textAlign: "center", pb: 5 }}>
              {error && <Alert severity="error" sx={{ mb: 3, textAlign: isAr ? "right" : "left" }}>{error}</Alert>}
              <Button
                variant="contained" size="large"
                onClick={handleSubmit} disabled={submitting}
                sx={{ px: 10, py: 2, borderRadius: 10, bgcolor: purple, "&:hover": { bgcolor: purpleDark } }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : t('admission_form.submitBtn')}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default AdmissionFormPublic;
